/**
 * Default Job Templates by Industry
 *
 * These templates are shown during mobile onboarding to help users
 * quickly get started with common job descriptions.
 *
 * In the future, these can be managed via Settings in the admin panel.
 */
import { IndustryContext, JobType, JobLocationType, ShiftType, QuestionAnswerType, PublicPageTheme } from '@common/clientServerApi';
/** Convert plain text questions to QualificationQuestion objects for template definitions */
function q(texts) {
    return texts.map((text, i) => ({ id: `tpl_${i}`, text }));
}
export const DEFAULT_JOB_TEMPLATES = [
    // ===== LOGISTICS & WAREHOUSE =====
    {
        id: 'logistics-warehouse-associate',
        title: 'Warehouse Associate',
        icon: '📦',
        industry: IndustryContext.Logistics,
        descriptionText: 'Pick, pack, and ship customer orders accurately while maintaining inventory organization and cleanliness.',
        responsibilities: [
            'Pick, pack, and ship customer orders accurately',
            'Load/unload trucks using pallet jacks/forklifts',
            'Maintain inventory organization and cleanliness',
            'Meet daily picking quotas (100+ orders)',
            'Follow safety protocols and OSHA standards',
        ],
        requirements: [
            'Ability to lift 50+ lbs repeatedly',
            'Stand/walk 8+ hours per shift',
            'Basic math and computer skills (WMS/scanners)',
            'Reliable transportation and good attendance',
            'High school diploma or equivalent',
        ],
        schedule: '4/10s or 5/8s, overtime available',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Can you lift 50+ lbs repeatedly throughout a shift?',
            'Do you have reliable transportation to the warehouse?',
            'Are you comfortable standing/walking for 8+ hours?',
            'Are you available to work overtime when needed?',
            'Do you have experience using warehouse scanners or WMS systems?',
        ]),
    },
    {
        id: 'logistics-forklift-operator',
        title: 'Forklift Operator',
        icon: '🚜',
        industry: IndustryContext.Logistics,
        descriptionText: 'Operate forklifts and other material handling equipment to move, locate, relocate, stack, and count merchandise.',
        responsibilities: [
            'Operate sit-down and stand-up forklifts safely',
            'Load and unload trucks and trailers',
            'Move and stack pallets in warehouse locations',
            'Perform equipment inspections and maintenance checks',
            'Maintain accurate inventory records',
        ],
        requirements: [
            'Valid forklift certification required',
            '1+ years forklift experience',
            'Ability to lift 50+ lbs',
            'Strong attention to detail',
            'Good communication skills',
        ],
        schedule: 'Day and night shifts available',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have a valid forklift certification?',
            'How many years of forklift operating experience do you have?',
            'Can you operate both sit-down and stand-up forklifts?',
            'Are you comfortable working in a cold storage environment?',
            'Do you have reliable transportation to the facility?',
        ]),
    },
    {
        id: 'logistics-cdl-driver',
        title: 'CDL Driver',
        icon: '🚛',
        industry: IndustryContext.Logistics,
        descriptionText: 'Transport goods and materials between locations while ensuring safe and timely deliveries.',
        responsibilities: [
            'Drive commercial vehicles to transport goods',
            'Load and unload cargo safely',
            'Maintain accurate delivery logs and paperwork',
            'Perform pre-trip and post-trip inspections',
            'Follow DOT regulations and safety protocols',
        ],
        requirements: [
            'Valid CDL Class A or B license',
            'Clean driving record (past 3 years)',
            '2+ years commercial driving experience',
            'Ability to pass DOT physical and drug test',
            'Strong navigation and time management skills',
        ],
        schedule: 'Local routes, home daily',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have a valid CDL Class A or B license?',
            'Is your driving record clean for the past 3 years?',
            'Can you pass a DOT physical and drug screening?',
            'Are you comfortable with local routes and home-daily schedules?',
            'Do you have experience with electronic logging devices (ELD)?',
        ]),
    },
    {
        id: 'logistics-shipping-receiving',
        title: 'Shipping & Receiving',
        icon: '📋',
        industry: IndustryContext.Logistics,
        descriptionText: 'Coordinate incoming and outgoing shipments, verify inventory, and maintain shipping documentation.',
        responsibilities: [
            'Process incoming and outgoing shipments',
            'Verify shipment contents against documentation',
            'Maintain accurate inventory records in WMS',
            'Coordinate with carriers for pickup/delivery',
            'Prepare shipping labels and documentation',
        ],
        requirements: [
            'Experience with shipping software and WMS',
            'Strong organizational and data entry skills',
            'Ability to lift 30+ lbs',
            'Basic computer proficiency (Excel, email)',
            'Excellent attention to detail',
        ],
        schedule: 'Monday-Friday, day shift',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have experience with shipping/receiving documentation?',
            'Are you comfortable using inventory management software?',
            'Can you lift and move packages up to 50 lbs?',
            'Are you detail-oriented with paperwork and record-keeping?',
            'Do you have reliable transportation?',
        ]),
    },
    {
        id: 'logistics-inventory-specialist',
        title: 'Inventory Specialist',
        icon: '📊',
        industry: IndustryContext.Logistics,
        descriptionText: 'Maintain accurate inventory records, conduct cycle counts, and resolve discrepancies.',
        responsibilities: [
            'Conduct regular cycle counts and audits',
            'Investigate and resolve inventory discrepancies',
            'Update inventory management systems',
            'Generate inventory reports and analysis',
            'Coordinate with purchasing on stock levels',
        ],
        requirements: [
            'Experience with inventory management systems',
            'Strong analytical and problem-solving skills',
            'Proficiency in Excel and data analysis',
            'Excellent attention to detail',
            'Good communication skills',
        ],
        schedule: 'Monday-Friday',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have experience with cycle counting or inventory audits?',
            'Are you proficient with inventory management systems or Excel?',
            'Can you work in a fast-paced warehouse environment?',
            'Are you available for early morning or evening shifts?',
            'Do you have experience with barcode scanning equipment?',
        ]),
    },
    // ===== RETAIL =====
    {
        id: 'retail-sales-associate',
        title: 'Sales Associate',
        icon: '🛍️',
        industry: IndustryContext.Retail,
        descriptionText: 'Assist customers with product selection, process transactions, and maintain store appearance.',
        responsibilities: [
            'Greet and assist customers with product selection',
            'Process sales transactions and returns',
            'Maintain store cleanliness and organization',
            'Restock merchandise and maintain displays',
            'Meet individual and team sales goals',
        ],
        requirements: [
            'Previous retail or customer service experience',
            'Strong communication and interpersonal skills',
            'Basic math skills for cash handling',
            'Ability to stand for extended periods',
            'Flexible schedule including weekends',
        ],
        schedule: 'Part-time and full-time available',
        jobType: JobType.PartTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Are you available to work weekends and holidays?',
            'Do you have experience with POS systems?',
            'Are you comfortable working on your feet for extended periods?',
            'Can you start within the next two weeks?',
            'Do you have reliable transportation to the store?',
        ]),
    },
    {
        id: 'retail-store-manager',
        title: 'Store Manager',
        icon: '👔',
        industry: IndustryContext.Retail,
        descriptionText: 'Lead store operations, manage staff, and drive sales performance to achieve business goals.',
        responsibilities: [
            'Oversee daily store operations',
            'Hire, train, and develop team members',
            'Drive sales and meet revenue targets',
            'Manage inventory and loss prevention',
            'Ensure excellent customer experience',
        ],
        requirements: [
            '3+ years retail management experience',
            'Proven track record of meeting sales targets',
            'Strong leadership and team-building skills',
            'Experience with inventory management',
            'Excellent problem-solving abilities',
        ],
        schedule: 'Full-time, flexible hours required',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'How many years of retail management experience do you have?',
            'Have you managed a team of 10+ employees?',
            'Are you comfortable with opening and closing responsibilities?',
            'Do you have experience with inventory management and loss prevention?',
            'Are you available to work flexible hours including weekends?',
        ]),
    },
    {
        id: 'retail-cashier',
        title: 'Cashier',
        icon: '💵',
        industry: IndustryContext.Retail,
        descriptionText: 'Process customer transactions efficiently while providing friendly service and maintaining cash accuracy.',
        responsibilities: [
            'Process cash, credit, and debit transactions',
            'Handle returns and exchanges',
            'Maintain accurate cash drawer balance',
            'Answer customer questions about products',
            'Keep checkout area clean and organized',
        ],
        requirements: [
            'Basic math and counting skills',
            'Friendly and professional demeanor',
            'Ability to stand for long periods',
            'Attention to detail and accuracy',
            'Previous cash handling experience preferred',
        ],
        schedule: 'Flexible shifts available',
        jobType: JobType.PartTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have experience handling cash and making change?',
            'Are you available for morning, evening, and weekend shifts?',
            'Are you comfortable standing for long periods?',
            'Can you start within the next two weeks?',
            'Do you have reliable transportation?',
        ]),
    },
    {
        id: 'retail-stock-associate',
        title: 'Stock Associate',
        icon: '📦',
        industry: IndustryContext.Retail,
        descriptionText: 'Receive, unpack, and organize merchandise while maintaining backroom inventory and restocking sales floor.',
        responsibilities: [
            'Unload delivery trucks and process shipments',
            'Organize and maintain backroom inventory',
            'Restock shelves and displays on sales floor',
            'Assist with inventory counts and audits',
            'Ensure product placement follows planograms',
        ],
        requirements: [
            'Ability to lift 50+ lbs repeatedly',
            'Strong organizational skills',
            'Ability to work early morning shifts',
            'Basic reading and counting skills',
            'Team player mentality',
        ],
        schedule: 'Early morning shifts, 4am-12pm',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Can you lift and carry boxes up to 40 lbs?',
            'Are you available for early morning stocking shifts?',
            'Do you have experience with inventory organization?',
            'Are you comfortable using a step ladder?',
            'Do you have reliable transportation?',
        ]),
    },
    {
        id: 'retail-assistant-manager',
        title: 'Assistant Store Manager',
        icon: '👥',
        industry: IndustryContext.Retail,
        descriptionText: 'Support store manager in daily operations, supervise team members, and ensure excellent customer service.',
        responsibilities: [
            'Supervise and coach sales team members',
            'Open and close store as manager on duty',
            'Handle customer escalations and complaints',
            'Assist with scheduling and payroll',
            'Support inventory management and loss prevention',
        ],
        requirements: [
            '2+ years retail experience',
            'Previous supervisory experience preferred',
            'Strong leadership and communication skills',
            'Ability to work flexible schedule',
            'Proficiency with POS and inventory systems',
        ],
        schedule: 'Full-time, rotating schedule',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 2,
        qualificationQuestions: q([
            'Do you have previous supervisory or team lead experience?',
            'Are you comfortable handling customer escalations?',
            'Can you work flexible hours including evenings and weekends?',
            'Do you have experience with scheduling and shift management?',
            'Are you familiar with retail reporting and sales metrics?',
        ]),
    },
    // ===== HOSPITALITY =====
    {
        id: 'hospitality-server',
        title: 'Server',
        icon: '🍽️',
        industry: IndustryContext.Hospitality,
        descriptionText: 'Provide exceptional dining experiences by taking orders, serving food, and ensuring guest satisfaction.',
        responsibilities: [
            'Greet guests and take food/beverage orders',
            'Serve food and drinks promptly and accurately',
            'Check on guests and address any concerns',
            'Process payments and handle cash/cards',
            'Maintain cleanliness of serving areas',
        ],
        requirements: [
            'Previous serving experience preferred',
            'Strong communication and memory skills',
            'Ability to work in fast-paced environment',
            'Must be 18+ (21+ for alcohol service)',
            'Flexible schedule including evenings/weekends',
        ],
        schedule: 'Evenings and weekends required',
        jobType: JobType.PartTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have a current food handler certification?',
            'Are you available to work evenings, weekends, and holidays?',
            'Can you carry heavy trays and stay on your feet for long shifts?',
            'Do you have experience with POS systems like Toast or Aloha?',
            'Are you 21+ (required for serving alcohol)?',
        ]),
    },
    {
        id: 'hospitality-line-cook',
        title: 'Line Cook',
        icon: '👨‍🍳',
        industry: IndustryContext.Hospitality,
        descriptionText: 'Prepare and cook menu items according to recipes while maintaining food quality and kitchen cleanliness.',
        responsibilities: [
            'Prepare ingredients and cook menu items',
            'Follow recipes and portion standards',
            'Maintain food safety and sanitation standards',
            'Stock and organize cooking stations',
            'Assist with kitchen cleanup and closing duties',
        ],
        requirements: [
            '1+ years line cook experience',
            'Food handler certification required',
            'Ability to work in hot, fast-paced environment',
            'Strong knife skills and cooking techniques',
            'Team player with good communication',
        ],
        schedule: 'Day and evening shifts available',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have a current food handler or ServSafe certification?',
            'How many years of professional kitchen experience do you have?',
            'Are you comfortable working in a high-heat, fast-paced environment?',
            'Are you available for evening and weekend shifts?',
            'Do you have experience with any specific cuisine types?',
        ]),
    },
    {
        id: 'hospitality-front-desk',
        title: 'Front Desk Agent',
        icon: '🏨',
        industry: IndustryContext.Hospitality,
        descriptionText: 'Welcome guests, handle check-ins/check-outs, and provide excellent customer service throughout their stay.',
        responsibilities: [
            'Check guests in and out efficiently',
            'Answer phone inquiries and make reservations',
            'Handle guest requests and resolve complaints',
            'Process payments and maintain accurate records',
            'Provide local area information and recommendations',
        ],
        requirements: [
            'Previous hospitality or customer service experience',
            'Excellent communication and problem-solving skills',
            'Proficiency with reservation systems',
            'Professional appearance and demeanor',
            'Flexible schedule including nights/weekends',
        ],
        schedule: 'Rotating shifts, 24/7 operation',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have experience with hotel property management systems?',
            'Are you comfortable working overnight or early morning shifts?',
            'Are you fluent in any languages besides English?',
            'Can you handle guest complaints calmly and professionally?',
            'Do you have reliable transportation for early/late shifts?',
        ]),
    },
    {
        id: 'hospitality-bartender',
        title: 'Bartender',
        icon: '🍸',
        industry: IndustryContext.Hospitality,
        descriptionText: 'Mix and serve alcoholic and non-alcoholic beverages while providing excellent guest service at the bar.',
        responsibilities: [
            'Prepare cocktails, beer, and wine orders',
            'Check IDs and verify legal drinking age',
            'Maintain clean and organized bar area',
            'Process payments and handle cash drawer',
            'Build rapport with guests and upsell menu items',
        ],
        requirements: [
            'Must be 21+ years old',
            'Bartending experience or certification',
            'Knowledge of classic and craft cocktails',
            'Strong multitasking abilities',
            'Excellent customer service skills',
        ],
        schedule: 'Evenings and weekends',
        jobType: JobType.PartTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 1,
        qualificationQuestions: q([
            'Do you have a valid bartending license or TIPS certification?',
            'Are you 21 or older?',
            'How many years of bartending experience do you have?',
            'Are you available to work late nights, weekends, and holidays?',
            'Are you familiar with craft cocktails and wine service?',
        ]),
    },
    {
        id: 'hospitality-host',
        title: 'Host / Hostess',
        icon: '📋',
        industry: IndustryContext.Hospitality,
        descriptionText: 'Welcome guests, manage reservations, and coordinate seating to ensure smooth dining room operations.',
        responsibilities: [
            'Greet and seat guests in a timely manner',
            'Manage reservation system and waitlist',
            'Answer phone calls and take reservations',
            'Maintain cleanliness of entrance and waiting area',
            'Communicate wait times to guests',
        ],
        requirements: [
            'Friendly and professional demeanor',
            'Strong organizational skills',
            'Ability to remain calm under pressure',
            'Basic computer skills for reservation systems',
            'Flexible schedule availability',
        ],
        schedule: 'Part-time, evenings and weekends',
        jobType: JobType.PartTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Are you available to work evenings and weekends?',
            'Are you comfortable standing and greeting guests for extended periods?',
            'Do you have experience with reservation systems like OpenTable?',
            'Can you handle a fast-paced environment during peak hours?',
            'Do you have reliable transportation?',
        ]),
    },
    // ===== DELIVERY =====
    {
        id: 'delivery-driver',
        title: 'Delivery Driver',
        icon: '🚗',
        industry: IndustryContext.Delivery,
        descriptionText: 'Deliver packages and goods to customers while maintaining excellent service and time efficiency.',
        responsibilities: [
            'Deliver packages to residential and business addresses',
            'Load and organize delivery vehicle',
            'Plan efficient delivery routes',
            'Obtain customer signatures and handle COD payments',
            'Report any delivery issues or discrepancies',
        ],
        requirements: [
            'Valid driver\'s license with clean record',
            'Reliable personal vehicle (if required)',
            'Smartphone with GPS capability',
            'Ability to lift packages up to 50 lbs',
            'Strong time management skills',
        ],
        schedule: 'Flexible hours, full and part-time available',
        jobType: JobType.Contract,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have a valid driver\'s license and clean driving record?',
            'Do you have your own reliable vehicle?',
            'Are you comfortable lifting packages up to 50 lbs?',
            'Are you familiar with GPS navigation and delivery apps?',
            'Can you pass a background check and drug screening?',
        ]),
    },
    {
        id: 'delivery-courier',
        title: 'Courier',
        icon: '🏃',
        industry: IndustryContext.Delivery,
        descriptionText: 'Provide fast and reliable same-day delivery services for documents and small packages.',
        responsibilities: [
            'Pick up and deliver time-sensitive materials',
            'Plan optimal routes for multiple stops',
            'Maintain delivery logs and documentation',
            'Handle packages with care and confidentiality',
            'Communicate with dispatch and customers',
        ],
        requirements: [
            'Valid driver\'s license or reliable transportation',
            'Knowledge of local area and streets',
            'Professional appearance and demeanor',
            'Strong punctuality and reliability',
            'Ability to work independently',
        ],
        schedule: 'Day shifts, Monday-Friday',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have a valid driver\'s license?',
            'Do you have your own vehicle (car, bike, or scooter)?',
            'Are you available for flexible scheduling including weekends?',
            'Are you comfortable navigating urban areas?',
            'Can you pass a background check?',
        ]),
    },
    {
        id: 'delivery-food-delivery',
        title: 'Food Delivery Driver',
        icon: '🍔',
        industry: IndustryContext.Delivery,
        descriptionText: 'Deliver food orders from restaurants to customers while ensuring timely and accurate deliveries.',
        responsibilities: [
            'Pick up orders from restaurant partners',
            'Deliver food to customers safely and promptly',
            'Maintain hot/cold food temperatures during transit',
            'Use delivery app for navigation and updates',
            'Provide friendly customer interactions',
        ],
        requirements: [
            'Valid driver\'s license and insurance',
            'Reliable vehicle (car, bike, or scooter)',
            'Smartphone with data plan',
            'Good knowledge of local area',
            'Ability to work flexible hours',
        ],
        schedule: 'Flexible, peak hours lunch/dinner',
        jobType: JobType.Contract,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have a valid driver\'s license and reliable vehicle?',
            'Are you available during peak meal hours (lunch and dinner)?',
            'Do you have a smartphone for delivery app navigation?',
            'Are you comfortable making multiple stops per hour?',
            'Do you have a clean driving record?',
        ]),
    },
    {
        id: 'delivery-package-handler',
        title: 'Package Handler',
        icon: '📬',
        industry: IndustryContext.Delivery,
        descriptionText: 'Sort, load, and unload packages at distribution centers to ensure accurate and timely deliveries.',
        responsibilities: [
            'Load and unload packages from delivery vehicles',
            'Sort packages by destination and route',
            'Scan packages and update tracking systems',
            'Maintain safe and organized work area',
            'Meet productivity and accuracy standards',
        ],
        requirements: [
            'Ability to lift 70+ lbs',
            'Stand and move for entire shift',
            'Basic reading and scanning skills',
            'Reliable attendance',
            'Ability to work early morning or overnight shifts',
        ],
        schedule: 'Early morning or overnight shifts',
        jobType: JobType.PartTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Can you lift and move packages weighing up to 70 lbs?',
            'Are you available for early morning or late night shifts?',
            'Are you comfortable working in a physically demanding role?',
            'Do you have reliable transportation to the sorting facility?',
            'Can you pass a background check and drug screening?',
        ]),
    },
    {
        id: 'delivery-route-driver',
        title: 'Route Delivery Driver',
        icon: '🚚',
        industry: IndustryContext.Delivery,
        descriptionText: 'Deliver products along an established route to business and residential customers.',
        responsibilities: [
            'Drive delivery vehicle along assigned route',
            'Deliver products and collect signatures',
            'Build relationships with regular customers',
            'Handle cash and check payments',
            'Maintain vehicle cleanliness and report issues',
        ],
        requirements: [
            'Valid driver\'s license with clean record',
            'Ability to drive box truck or cargo van',
            'Customer service skills',
            'Physical ability to lift and carry products',
            'Early morning availability',
        ],
        schedule: 'Monday-Friday, early start',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have a valid commercial driver\'s license?',
            'Is your driving record clean for the past 3 years?',
            'Are you comfortable driving large vehicles (box trucks/vans)?',
            'Can you pass a DOT physical if required?',
            'Are you available for early morning start times?',
        ]),
    },
    // ===== E-COMMERCE =====
    {
        id: 'ecommerce-fulfillment-associate',
        title: 'Fulfillment Associate',
        icon: '📦',
        industry: IndustryContext.Ecommerce,
        descriptionText: 'Process online orders accurately and efficiently in a fast-paced fulfillment environment.',
        responsibilities: [
            'Pick and pack customer orders accurately',
            'Scan items and update inventory systems',
            'Meet productivity and quality standards',
            'Prepare packages for shipping',
            'Maintain clean and organized work area',
        ],
        requirements: [
            'Ability to stand/walk for 8+ hours',
            'Basic computer and scanner proficiency',
            'Attention to detail and accuracy',
            'Ability to lift up to 40 lbs',
            'Reliable attendance record',
        ],
        schedule: 'Multiple shifts available',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Are you comfortable working on your feet for 8+ hours?',
            'Can you meet daily picking and packing quotas?',
            'Do you have experience with warehouse management systems?',
            'Are you available for overtime during peak seasons?',
            'Do you have reliable transportation to the fulfillment center?',
        ]),
    },
    {
        id: 'ecommerce-customer-service',
        title: 'Customer Service Rep',
        icon: '💬',
        industry: IndustryContext.Ecommerce,
        descriptionText: 'Assist online customers with orders, returns, and inquiries via chat, email, and phone.',
        responsibilities: [
            'Respond to customer inquiries via multiple channels',
            'Process returns and exchanges',
            'Track and update order status',
            'Resolve customer complaints professionally',
            'Document customer interactions in CRM',
        ],
        requirements: [
            'Previous customer service experience',
            'Excellent written and verbal communication',
            'Proficiency with computer systems',
            'Problem-solving and patience',
            'Ability to multitask effectively',
        ],
        schedule: 'Remote available, flexible hours',
        jobType: JobType.FullTime,
        locationType: JobLocationType.FullyRemote,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have experience with customer service software (Zendesk, etc.)?',
            'Are you comfortable handling phone, email, and chat inquiries?',
            'Can you type at least 40 words per minute?',
            'Are you available for rotating shifts including weekends?',
            'Do you have a quiet home workspace for remote work?',
        ]),
    },
    {
        id: 'ecommerce-returns-processor',
        title: 'Returns Processor',
        icon: '↩️',
        industry: IndustryContext.Ecommerce,
        descriptionText: 'Process customer returns, inspect merchandise, and update inventory systems for restocking or disposal.',
        responsibilities: [
            'Receive and inspect returned merchandise',
            'Determine item condition and disposition',
            'Process refunds and exchanges in system',
            'Restock sellable items to inventory',
            'Maintain organized returns processing area',
        ],
        requirements: [
            'Attention to detail and accuracy',
            'Basic computer and data entry skills',
            'Ability to stand for extended periods',
            'Problem-solving abilities',
            'Previous warehouse experience helpful',
        ],
        schedule: 'Day shift, Monday-Friday',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have experience with returns processing or quality inspection?',
            'Are you detail-oriented when inspecting returned items?',
            'Can you work in a fast-paced environment with daily quotas?',
            'Are you comfortable using computers and scanning equipment?',
            'Do you have reliable transportation?',
        ]),
    },
    {
        id: 'ecommerce-inventory-control',
        title: 'Inventory Control Specialist',
        icon: '📊',
        industry: IndustryContext.Ecommerce,
        descriptionText: 'Maintain accurate inventory counts, investigate discrepancies, and optimize stock levels for online orders.',
        responsibilities: [
            'Conduct daily and weekly cycle counts',
            'Investigate and resolve inventory variances',
            'Update inventory management systems',
            'Generate reports on stock levels and trends',
            'Coordinate with purchasing on reorder points',
        ],
        requirements: [
            'Experience with inventory management systems',
            'Strong Excel and analytical skills',
            'Attention to detail',
            'Problem-solving abilities',
            'Previous e-commerce or warehouse experience',
        ],
        schedule: 'Monday-Friday',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 1,
        qualificationQuestions: q([
            'Do you have experience with inventory management systems?',
            'Are you proficient with Excel or Google Sheets?',
            'Have you conducted cycle counts or inventory audits before?',
            'Are you detail-oriented with data entry and record-keeping?',
            'Can you work independently with minimal supervision?',
        ]),
    },
    {
        id: 'ecommerce-quality-control',
        title: 'Quality Control Inspector',
        icon: '✅',
        industry: IndustryContext.Ecommerce,
        descriptionText: 'Inspect incoming and outgoing products to ensure quality standards are met before shipping to customers.',
        responsibilities: [
            'Inspect products for defects and damage',
            'Verify product specifications and labeling',
            'Document and report quality issues',
            'Work with vendors on quality concerns',
            'Maintain quality control records',
        ],
        requirements: [
            'Strong attention to detail',
            'Quality control or inspection experience',
            'Basic computer skills for documentation',
            'Ability to stand and inspect products all day',
            'Good communication skills',
        ],
        schedule: 'Day shift',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have experience with quality inspection processes?',
            'Are you familiar with quality standards (ISO, Six Sigma)?',
            'Can you identify product defects and document findings accurately?',
            'Are you comfortable using measurement and testing tools?',
            'Do you have experience writing quality reports?',
        ]),
    },
    // ===== SERVICES =====
    {
        id: 'services-technician',
        title: 'Service Technician',
        icon: '🔧',
        industry: IndustryContext.Services,
        descriptionText: 'Perform maintenance, repairs, and installations for residential and commercial customers.',
        responsibilities: [
            'Diagnose and repair equipment issues',
            'Perform routine maintenance and inspections',
            'Install new equipment and systems',
            'Document work performed and parts used',
            'Provide excellent customer service',
        ],
        requirements: [
            'Technical training or certification',
            '2+ years hands-on experience',
            'Valid driver\'s license and reliable vehicle',
            'Strong troubleshooting skills',
            'Good communication with customers',
        ],
        schedule: 'Monday-Friday, some on-call',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have a valid driver\'s license and reliable vehicle?',
            'Do you have relevant trade certifications or licenses?',
            'Are you comfortable working in various weather conditions?',
            'Can you pass a background check for residential work?',
            'Do you have your own basic tool set?',
        ]),
    },
    {
        id: 'services-cleaner',
        title: 'Cleaner / Housekeeper',
        icon: '🧹',
        industry: IndustryContext.Services,
        descriptionText: 'Maintain cleanliness and organization of residential or commercial spaces.',
        responsibilities: [
            'Clean and sanitize all areas thoroughly',
            'Vacuum, mop, and dust surfaces',
            'Empty trash and replace liners',
            'Restock supplies as needed',
            'Report any maintenance issues',
        ],
        requirements: [
            'Attention to detail and quality standards',
            'Ability to work independently',
            'Physical stamina for cleaning tasks',
            'Reliable transportation',
            'Background check required',
        ],
        schedule: 'Flexible scheduling available',
        jobType: JobType.PartTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have reliable transportation to job sites?',
            'Are you comfortable with physical tasks (bending, lifting, standing)?',
            'Can you pass a background check for residential/commercial work?',
            'Are you available for flexible scheduling including weekends?',
            'Do you have experience with commercial cleaning equipment?',
        ]),
    },
    {
        id: 'services-landscaper',
        title: 'Landscaper / Groundskeeper',
        icon: '🌿',
        industry: IndustryContext.Services,
        descriptionText: 'Maintain outdoor spaces including lawns, gardens, and grounds for residential and commercial properties.',
        responsibilities: [
            'Mow lawns and trim edges',
            'Plant, water, and maintain gardens',
            'Prune shrubs and trees',
            'Apply fertilizers and pest control',
            'Clean up debris and maintain equipment',
        ],
        requirements: [
            'Physical ability for outdoor work',
            'Knowledge of plants and lawn care',
            'Ability to operate landscaping equipment',
            'Valid driver\'s license',
            'Work well in varying weather conditions',
        ],
        schedule: 'Monday-Friday, seasonal overtime',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have experience operating landscaping equipment (mowers, trimmers)?',
            'Are you comfortable working outdoors in various weather conditions?',
            'Do you have a valid driver\'s license?',
            'Can you perform physically demanding work for full shifts?',
            'Are you available for early morning start times?',
        ]),
    },
    {
        id: 'services-hvac-technician',
        title: 'HVAC Technician',
        icon: '❄️',
        industry: IndustryContext.Services,
        descriptionText: 'Install, maintain, and repair heating, ventilation, and air conditioning systems.',
        responsibilities: [
            'Install new HVAC systems and equipment',
            'Diagnose and repair system issues',
            'Perform preventive maintenance',
            'Test systems for proper operation',
            'Advise customers on system care',
        ],
        requirements: [
            'HVAC certification or license',
            '2+ years HVAC experience',
            'EPA 608 certification',
            'Valid driver\'s license',
            'Strong troubleshooting skills',
        ],
        schedule: 'Monday-Friday, on-call rotation',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 2,
        qualificationQuestions: q([
            'Do you have EPA 608 certification?',
            'How many years of HVAC experience do you have?',
            'Do you have a valid driver\'s license and reliable vehicle?',
            'Are you comfortable working in attics, crawl spaces, and on rooftops?',
            'Can you pass a background check and drug screening?',
        ]),
    },
    {
        id: 'services-electrician',
        title: 'Electrician',
        icon: '⚡',
        industry: IndustryContext.Services,
        descriptionText: 'Install, maintain, and repair electrical systems in residential and commercial buildings.',
        responsibilities: [
            'Install wiring, outlets, and fixtures',
            'Diagnose and repair electrical problems',
            'Read and interpret blueprints',
            'Ensure work meets electrical codes',
            'Test circuits and equipment',
        ],
        requirements: [
            'Journeyman or Master Electrician license',
            '3+ years electrical experience',
            'Knowledge of NEC codes',
            'Valid driver\'s license',
            'Ability to work in various conditions',
        ],
        schedule: 'Monday-Friday, emergency on-call',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 3,
        qualificationQuestions: q([
            'Do you have a valid electrician license (Journeyman or Master)?',
            'How many years of electrical experience do you have?',
            'Are you comfortable working at heights and in confined spaces?',
            'Do you have a valid driver\'s license and reliable transportation?',
            'Can you pass a background check and drug screening?',
        ]),
    },
    // ===== BEAUTY =====
    {
        id: 'services-nail-technician',
        title: 'Nail Technician / Manicure Master',
        icon: '💅',
        industry: IndustryContext.Services,
        descriptionText: 'Deliver premium manicure, pedicure, and nail-art services in a busy salon while keeping clients happy and rebooking.',
        responsibilities: [
            'Perform manicures, pedicures, gel, and acrylic services',
            'Design custom nail art per client requests',
            'Sanitize tools and workstation between clients',
            'Recommend products and rebook clients',
            'Keep detailed notes on client preferences and allergies',
        ],
        requirements: [
            'Valid nail technician license (state-issued)',
            '1+ year salon experience preferred',
            'Portfolio of nail art / recent work',
            'Steady hand and strong attention to detail',
            'Friendly, client-first communication style',
        ],
        schedule: 'Tuesday-Saturday, evening shifts available',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 1,
        brandingTheme: PublicPageTheme.ElegantSalon,
        applyFormConfig: {
            splitName: true,
            showExperienceStep: false,
            showAdditionalInfo: true,
        },
        qualificationQuestions: [
            {
                id: 'tpl_license',
                text: 'Do you have an active state Nail Technician License?',
                answerType: QuestionAnswerType.YesNo,
                required: true,
                askOnForm: true,
                isKnockout: true,
                knockoutAnswer: 'No',
            },
            {
                id: 'tpl_workauth',
                text: 'Are you legally authorized to work in the United States?',
                answerType: QuestionAnswerType.YesNo,
                required: true,
                askOnForm: true,
                isKnockout: true,
                knockoutAnswer: 'No',
            },
            {
                id: 'tpl_portfolio',
                text: 'Portfolio / Working Instagram',
                answerType: QuestionAnswerType.Url,
                required: true,
                askOnForm: true,
                helpText: 'Please provide a link to photos of your work or your professional Instagram account.',
                placeholder: 'https://instagram.com/...',
            },
            { id: 'tpl_systems', text: 'Are you experienced with gel, acrylic, and dip powder systems?' },
            { id: 'tpl_weekend', text: 'Are you available for weekend and evening shifts?' },
        ],
    },
    {
        id: 'services-hair-stylist',
        title: 'Hair Stylist',
        icon: '💇',
        industry: IndustryContext.Services,
        descriptionText: 'Cut, color, and style hair for a loyal client base in a high-end salon environment.',
        responsibilities: [
            'Perform cuts, color, highlights, and blowouts',
            'Consult with clients on style and hair health',
            'Maintain a clean, sanitized station',
            'Recommend home-care products',
            'Build and retain a personal book of clients',
        ],
        requirements: [
            'Valid cosmetology license',
            '2+ years salon experience',
            'Strong color theory and cutting technique',
            'Portfolio of before/after work',
            'Excellent client-service skills',
        ],
        schedule: 'Tuesday-Saturday, commission + tips',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 2,
        qualificationQuestions: q([
            'Do you hold a current cosmetology license?',
            'How many years of salon experience do you have?',
            'Are you confident with color correction and balayage?',
            'Do you have an existing book of clients you can bring?',
            'Are you available Tuesday through Saturday?',
        ]),
    },
    {
        id: 'services-barber',
        title: 'Barber',
        icon: '💈',
        industry: IndustryContext.Services,
        descriptionText: 'Deliver precision cuts, fades, and beard grooming in a modern barbershop.',
        responsibilities: [
            'Perform men\'s cuts, fades, tapers, and beard trims',
            'Consult on style and grooming routines',
            'Sanitize clippers and workstation between clients',
            'Sell grooming products',
            'Retain and grow a personal client base',
        ],
        requirements: [
            'Valid barber license',
            '1+ year barbershop experience',
            'Skilled in modern fade and taper techniques',
            'Reliable and punctual',
            'Strong customer-service skills',
        ],
        schedule: 'Tuesday-Saturday, walk-ins + appointments',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 1,
        qualificationQuestions: q([
            'Do you hold a current state barber license?',
            'How many years of professional barbering experience do you have?',
            'Are you skilled with clipper-over-comb fades and skin fades?',
            'Are you comfortable with beard shaping and straight-razor work?',
            'Are you available for Saturday shifts?',
        ]),
    },
    {
        id: 'services-esthetician',
        title: 'Esthetician',
        icon: '✨',
        industry: IndustryContext.Services,
        descriptionText: 'Provide facials, waxing, and skincare treatments while educating clients on skin health.',
        responsibilities: [
            'Perform facials, chemical peels, and skincare treatments',
            'Complete waxing services (brow, lip, body)',
            'Assess skin conditions and recommend regimens',
            'Sanitize room and tools between clients',
            'Retail skincare products and rebook clients',
        ],
        requirements: [
            'Valid esthetician license',
            '1+ year experience in spa or medspa',
            'Knowledge of skincare ingredients and protocols',
            'Excellent hygiene and sanitation habits',
            'Consultative sales skills',
        ],
        schedule: 'Tuesday-Saturday, some evenings',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 1,
        qualificationQuestions: q([
            'Do you hold a current state esthetician license?',
            'How many years of spa or medspa experience do you have?',
            'Are you certified in chemical peels or microdermabrasion?',
            'Are you comfortable performing full-body waxing?',
            'Do you have experience selling retail skincare?',
        ]),
    },
    {
        id: 'services-massage-therapist',
        title: 'Massage Therapist',
        icon: '💆',
        industry: IndustryContext.Services,
        descriptionText: 'Provide therapeutic and relaxation massage in a professional spa or wellness clinic.',
        responsibilities: [
            'Perform Swedish, deep-tissue, and prenatal massages',
            'Consult with clients on pressure and problem areas',
            'Prepare and clean treatment rooms',
            'Maintain client SOAP notes',
            'Recommend follow-up sessions and home care',
        ],
        requirements: [
            'Valid massage therapy license',
            'Certified in multiple modalities',
            '1+ year professional experience',
            'Strong physical stamina',
            'Warm, professional bedside manner',
        ],
        schedule: 'Flexible, includes weekends',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 1,
        qualificationQuestions: q([
            'Do you hold a current state massage therapy license?',
            'How many years of professional massage experience do you have?',
            'Which modalities are you certified in (Swedish, deep tissue, sports, prenatal)?',
            'Are you comfortable performing 5-6 sessions per day?',
            'Are you available for weekend shifts?',
        ]),
    },
    {
        id: 'services-makeup-artist',
        title: 'Makeup Artist',
        icon: '💄',
        industry: IndustryContext.Services,
        descriptionText: 'Create camera-ready, bridal, and event makeup looks for a diverse clientele.',
        responsibilities: [
            'Apply makeup for events, bridal, and photoshoots',
            'Consult with clients on desired look',
            'Maintain a sanitary, well-stocked kit',
            'Stay current on trends and techniques',
            'Build a portfolio and social presence',
        ],
        requirements: [
            'Cosmetology or esthetician license (state-dependent)',
            '2+ years professional makeup experience',
            'Portfolio across skin tones and looks',
            'Reliable transportation for on-site work',
            'Strong client-management skills',
        ],
        schedule: 'Event-based, weekends common',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 2,
        qualificationQuestions: q([
            'How many years of professional makeup experience do you have?',
            'Can you share a portfolio covering multiple skin tones?',
            'Do you have your own professional kit?',
            'Are you available for weekend and early-morning bridal calls?',
            'Do you have reliable transportation to on-site events?',
        ]),
    },
    // ===== GENERAL =====
    {
        id: 'general-administrative-assistant',
        title: 'Administrative Assistant',
        icon: '📋',
        industry: IndustryContext.General,
        descriptionText: 'Provide administrative support including scheduling, correspondence, and office management.',
        responsibilities: [
            'Manage calendars and schedule appointments',
            'Handle incoming calls and correspondence',
            'Prepare documents and presentations',
            'Maintain filing systems and records',
            'Order supplies and coordinate vendors',
        ],
        requirements: [
            'Proficiency in Microsoft Office Suite',
            'Excellent organizational skills',
            'Strong written and verbal communication',
            'Attention to detail and accuracy',
            'Previous administrative experience',
        ],
        schedule: 'Monday-Friday, 9-5',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Are you proficient with Microsoft Office or Google Workspace?',
            'Do you have experience managing calendars and scheduling?',
            'Can you type at least 50 words per minute?',
            'Are you comfortable answering phones and greeting visitors?',
            'Can you start within the next two weeks?',
        ]),
    },
    {
        id: 'general-customer-service',
        title: 'Customer Service Rep',
        icon: '📞',
        industry: IndustryContext.General,
        descriptionText: 'Assist customers with inquiries, complaints, and requests while providing excellent service.',
        responsibilities: [
            'Answer customer calls and emails promptly',
            'Resolve customer issues and complaints',
            'Process orders and returns',
            'Document customer interactions',
            'Escalate complex issues as needed',
        ],
        requirements: [
            'Strong communication skills',
            'Patience and empathy',
            'Basic computer proficiency',
            'Problem-solving abilities',
            'Ability to handle difficult situations',
        ],
        schedule: 'Various shifts available',
        jobType: JobType.FullTime,
        locationType: JobLocationType.Hybrid,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Do you have experience with CRM or ticketing systems?',
            'Are you comfortable handling difficult customer interactions?',
            'Can you work flexible hours including evenings and weekends?',
            'Do you have a quiet workspace for potential remote work?',
            'Are you fluent in any languages besides English?',
        ]),
    },
    {
        id: 'general-receptionist',
        title: 'Receptionist',
        icon: '🪪',
        industry: IndustryContext.General,
        descriptionText: 'Serve as the first point of contact for visitors and callers, managing the front desk operations.',
        responsibilities: [
            'Greet visitors and direct them appropriately',
            'Answer and route phone calls',
            'Manage visitor sign-in and badges',
            'Receive and distribute mail and packages',
            'Maintain front desk appearance',
        ],
        requirements: [
            'Professional appearance and demeanor',
            'Excellent phone and communication skills',
            'Basic computer proficiency',
            'Ability to multitask',
            'Previous receptionist experience helpful',
        ],
        schedule: 'Monday-Friday, 8-5',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Are you comfortable managing a multi-line phone system?',
            'Do you have experience greeting visitors in a professional setting?',
            'Are you proficient with scheduling software?',
            'Can you handle multiple tasks simultaneously?',
            'Do you have reliable transportation for consistent attendance?',
        ]),
    },
    {
        id: 'general-data-entry',
        title: 'Data Entry Clerk',
        icon: '⌨️',
        industry: IndustryContext.General,
        descriptionText: 'Enter, update, and maintain data in computer systems with accuracy and efficiency.',
        responsibilities: [
            'Enter data from various sources into systems',
            'Verify accuracy of data entered',
            'Update and maintain existing records',
            'Generate reports as needed',
            'Identify and correct data errors',
        ],
        requirements: [
            'Fast and accurate typing (50+ WPM)',
            'Strong attention to detail',
            'Proficiency with spreadsheets and databases',
            'Good organizational skills',
            'Ability to work independently',
        ],
        schedule: 'Monday-Friday, remote options',
        jobType: JobType.FullTime,
        locationType: JobLocationType.Hybrid,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 0,
        qualificationQuestions: q([
            'Can you type at least 60 words per minute with high accuracy?',
            'Are you proficient with Excel or Google Sheets?',
            'Are you comfortable working with repetitive data tasks?',
            'Do you have experience with data management systems or databases?',
            'Can you work independently and meet daily data entry targets?',
        ]),
    },
    {
        id: 'general-office-manager',
        title: 'Office Manager',
        icon: '🏢',
        industry: IndustryContext.General,
        descriptionText: 'Oversee daily office operations, manage administrative staff, and ensure efficient workplace functioning.',
        responsibilities: [
            'Manage office supplies and equipment',
            'Supervise administrative staff',
            'Coordinate office events and meetings',
            'Handle vendor relationships and contracts',
            'Maintain office policies and procedures',
        ],
        requirements: [
            '3+ years office management experience',
            'Strong organizational and leadership skills',
            'Proficiency with office software',
            'Budget management experience',
            'Excellent communication skills',
        ],
        schedule: 'Monday-Friday, 9-5',
        jobType: JobType.FullTime,
        locationType: JobLocationType.InPerson,
        location: 'US',
        shiftType: ShiftType.Day,
        minExperienceYears: 3,
        qualificationQuestions: q([
            'How many years of office management experience do you have?',
            'Have you managed office budgets and vendor relationships?',
            'Are you experienced with HR tasks like onboarding and payroll?',
            'Are you proficient with office management software?',
            'Can you handle multiple priorities and meet deadlines consistently?',
        ]),
    },
];
// Helper function to get templates by industry
export const getTemplatesByIndustry = (industry) => {
    // For general, show a mix of common jobs
    if (industry === IndustryContext.General) {
        return DEFAULT_JOB_TEMPLATES.filter(t => t.industry === IndustryContext.General);
    }
    return DEFAULT_JOB_TEMPLATES.filter(t => t.industry === industry);
};
// Helper function to get all available industries with templates
export const getIndustriesWithTemplates = () => {
    const industries = new Set(DEFAULT_JOB_TEMPLATES.map(t => t.industry));
    return Array.from(industries).filter((i) => i !== undefined);
};
